import React from "react"

import { Link, useStaticQuery, graphql } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"
import Image from "gatsby-plugin-sanity-image"
import PropTypes from "prop-types"

import * as styles from "./card.module.scss"
import DarkColor from "../../../helpers/dark-color"
import portabletextToPlain from "../../../helpers/portabletext-to-plain"
import Button from "../../atoms/button"
import Heading from "../../atoms/heading"
import Tag from "../../atoms/tag"

const Card = ({ article, variant, color }) => {
  const { allSanityDictionary } = useStaticQuery(graphql`
    {
      allSanityDictionary {
        nodes {
          key {
            current
          }
          value
        }
      }
    }
  `)

  let headingLevel
  if (variant === "primary_feature") {
    headingLevel = "2"
  } else if (variant === "secondary_feature") {
    headingLevel = "3"
  } else {
    headingLevel = "4"
  }

  let image
  const imageSource = article.featured_image?.image || article.image
  if (imageSource) {
    image = (
      <div
        className={
          variant === "tertiary_feature" ? styles.imageWrap__tertiary : ""
        }
      >
        <Image
          {...imageSource}
          width={variant === "primary_feature" ? 600 : 400}
          className={`${styles.image} ${
            variant === "primary_feature" ? styles.image__primary : ""
          } ${variant === "tertiary_feature" ? styles.image__tertiary : ""}`}
          alt={article.featured_image?.caption || article.title}
        />
      </div>
    )
  } else if (variant === "primary_feature" || variant === "tertiary_feature") {
    image = ""
  } else {
    image = (
      <StaticImage
        className={styles.image}
        src="../../../images/scenes/iphone.jpg"
        aspectRatio={16 / 9}
        alt=""
      />
    )
  }

  const cta = () => {
    if (article.type === "quiz") return "quiz_cta"
    if (article.type === "bracket") return "bracket_submit"
    if (article.type === "podcast") return "listen"
    return "read_more"
  }

  const url = () => {
    if (article.url) return article.url
    if (article.slug) {
      return `/${
        article.type === "bracket"
          ? "bracket-challenge"
          : article.web_article_type || article.type
      }/${article.slug.current}`
    }
    return null
  }

  const card = (
    <>
      {image}
      {variant === "labelled" && article.storyType ? (
        <div className={styles.storyType}>
          <Tag color="white">{article.storyType}</Tag>
        </div>
      ) : (
        ""
      )}
      <div
        className={`${styles.content} ${
          variant === "primary_feature" ? styles.content__primary : ""
        } ${variant === "tertiary_feature" ? styles.content__tertiary : ""}  ${
          !article.featuredImage &&
          (variant === "tertiary_feature" || variant === "primary_feature")
            ? styles.content__blankImage
            : ""
        }`}
      >
        {variant === "primary_feature" ||
        variant === "secondary_feature" ||
        article.partner ? (
          <div>
            {article.partner || article.vertical?.name || article.storyType ? (
              <div>
                <Tag partner={!!article.partner} light={DarkColor(color)}>
                  {article.partner ? (
                    <>
                      {article.partner.prefix ||
                        allSanityDictionary.nodes.find(
                          (item) => item.key.current === "partner_feature"
                        )?.value}
                      {article.partner.logo ? (
                        <Image
                          {...article.partner.logo}
                          alt={article.name}
                          width={100}
                        />
                      ) : (
                        <>&nbsp;{article.partner.name}</>
                      )}
                    </>
                  ) : (
                    article.vertical?.name || article.storyType
                  )}
                </Tag>
              </div>
            ) : (
              ""
            )}
          </div>
        ) : (
          ""
        )}
        <div
          className={`${styles.title} ${
            variant === "tertiary_feature" ? styles.title__tertiary : ""
          }
          ${
            article.sport?.length > 1 && variant !== "regular"
              ? styles.title__multisport
              : ""
          }`}
        >
          <Heading level={headingLevel} semantic={false}>
            {article.detailed_title || article.title}
          </Heading>
          {article.sport?.length > 0 ? (
            <div className={styles.emojis}>
              {article.sport.map((sport) => {
                if (sport.emoji)
                  return (
                    <span key={sport.emoji} className={styles.emoji}>
                      {sport.emoji}
                    </span>
                  )
                return ""
              })}
            </div>
          ) : (
            ""
          )}
        </div>
        {article.date && variant === "primary_feature" ? (
          <Heading level="6" semantic={false}>
            {new Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "long",
              day: "2-digit",
            }).format(
              new Date(
                article.date.includes("T")
                  ? article.date
                  : `${article.date}T00:00:00.000`
              )
            )}
          </Heading>
        ) : (
          ""
        )}
        {article._rawAbstract ? (
          <div>
            {portabletextToPlain(article._rawAbstract).substr(0, 160)}
            {portabletextToPlain(article._rawAbstract).length > 160
              ? "..."
              : ""}
          </div>
        ) : (
          <div>{article.abstract}</div>
        )}
        {variant === "primary_feature" && url() ? (
          <Button variant="secondary" url={url()}>
            {
              allSanityDictionary.nodes.find(
                (item) => item.key.current === cta()
              )?.value
            }
          </Button>
        ) : (
          ""
        )}
        {article.date && variant !== "primary_feature" ? (
          <Heading level="6" semantic={false}>
            {new Intl.DateTimeFormat("en-US", {
              year: "numeric",
              month: "long",
              day: "2-digit",
            }).format(
              new Date(
                article.date.includes("T")
                  ? article.date
                  : `${article.date}T00:00:00.000`
              )
            )}
          </Heading>
        ) : (
          ""
        )}
      </div>
    </>
  )
  if (variant === "primary_feature") {
    return (
      <div
        className={`${styles.card} ${styles.card__primary} ${
          color ? styles[`card__${color.replace("-", "")}`] : ""
        }`}
      >
        {card}
      </div>
    )
  }

  return url() ? (
    <Link
      to={url()}
      className={`${styles.card} ${styles.card__link} ${
        color ? styles[`card__${color.replace("-", "")}`] : ""
      } ${variant === "tertiary_feature" ? styles.card__tertiary : ""}  ${
        variant === "secondary_feature" ? styles.card__secondary : ""
      }`}
    >
      {card}
    </Link>
  ) : (
    <div
      className={`${styles.card} ${
        color ? styles[`card__${color.replace("-", "")}`] : ""
      } ${variant === "tertiary_feature" ? styles.card__tertiary : ""}  ${
        variant === "secondary_feature" ? styles.card__secondary : ""
      }`}
    >
      {card}
    </div>
  )
}

export default Card

Card.propTypes = {
  article: PropTypes.object.isRequired,
  variant: PropTypes.string,
  color: PropTypes.string,
}

Card.defaultProps = {
  variant: "regular",
  color: "",
}
